import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Phone, KeyRound, ArrowLeft, Loader2, User as UserIcon, Lock, Eye, EyeOff, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth, type User } from '@/lib/auth';

const SHOW_OTP = ((import.meta.env.VITE_SHOW_OTP_LOGIN as string) || 'false') === 'true';

type Mode = 'password' | 'otp_phone' | 'otp_code';

interface LoginResponse {
  accessToken: string;
  user: User & { mustChangePassword?: boolean };
}

/**
 * Sign in.
 *
 * Split screen: the company on the left, the form on the right. The brand panel
 * is decoration and hides on a phone, where a smaller mark leads instead - a
 * driver signing in on a handset needs the field, not the poster.
 *
 * Everything below the presentation is untouched: the same three modes, the
 * same endpoints, the same two redirects. This page is the front door, and a
 * redesign that locks people out is not a redesign.
 */
export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);

  const [mode, setMode] = useState<Mode>('password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode === 'otp_code') codeInputRef.current?.focus();
  }, [mode]);

  function afterLogin(data: LoginResponse) {
    setSession(data.accessToken, data.user);
    if (data.user.mustChangePassword) {
      navigate('/dashboard/change-password', { replace: true });
    } else {
      // PR-STEP2-3 — land on the three-tile project home.
      navigate('/home', { replace: true });
    }
  }

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await apiClient.post<LoginResponse>('/api/v1/auth/login', {
        username: username.trim(),
        password,
      });
      afterLogin(data);
    } catch (err) {
      const apiErr = (err as { response?: { status?: number; data?: { error?: string; message?: string } } }).response;
      if (apiErr?.status === 423 || apiErr?.data?.error === 'account_locked') {
        setError('Account locked. Try again in 15 minutes.');
      } else if (apiErr?.status === 401) {
        setError('Invalid username or password');
      } else {
        setError(extractErrorMessage(err));
      }
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRequestOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.post('/api/v1/auth/request-otp', { phone: phone.trim() });
      setMode('otp_code');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await apiClient.post<LoginResponse>('/api/v1/auth/verify-otp', {
        phone: phone.trim(),
        code: code.trim(),
      });
      afterLogin(data);
    } catch (err) {
      setError(extractErrorMessage(err));
      setCode('');
      codeInputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  /** One field, with its icon inside. Logical properties so Arabic mirrors. */
  const field =
    'h-11 w-full rounded-lg border border-[#E2E8F0] dark:border-white/10 '
    + 'bg-white dark:bg-white/5 text-[#0F172A] dark:text-slate-100 '
    + 'ps-10 pe-3 text-sm outline-none transition-all duration-200 '
    + 'placeholder:text-[#94A3B8] '
    + 'focus:border-[#C59B27] focus:ring-2 focus:ring-[#C59B27]/30';
  const iconWrap =
    'pointer-events-none absolute inset-y-0 start-3 flex items-center text-[#94A3B8]';

  const goldButton =
    'w-full h-11 rounded-lg bg-[#C59B27] text-white font-medium '
    + 'transition-all duration-200 hover:bg-[#A6811E] hover:shadow-md hover:shadow-amber-500/20 '
    + 'active:scale-[0.99] disabled:opacity-60 disabled:pointer-events-none '
    + 'inline-flex items-center justify-center gap-2';

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#FAFAFA] dark:bg-[#0F172A]">
      {/* ---------------------------------------------------- brand panel */}
      <aside className="relative hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-12 overflow-hidden">
        {/* The bridge, abstracted: two arcs in gold at low opacity. Drawn rather
            than an image so it scales and costs nothing to load. */}
        <svg aria-hidden className="absolute inset-0 w-full h-full opacity-[0.07]"
             viewBox="0 0 600 600" preserveAspectRatio="xMidYMid slice">
          <path d="M-40 420 C 150 250 450 250 640 420" fill="none" stroke="#C59B27" strokeWidth="18" />
          <path d="M-40 470 C 170 320 430 320 640 470" fill="none" stroke="#C59B27" strokeWidth="8" />
          <path d="M300 150 L300 400" stroke="#C59B27" strokeWidth="10" />
          <path d="M-40 430 C 140 300 240 200 300 152" fill="none" stroke="#C59B27" strokeWidth="4" />
          <path d="M640 430 C 460 300 360 200 300 152" fill="none" stroke="#C59B27" strokeWidth="4" />
        </svg>

        <div className="relative">
          <img src="/logo.png" alt="" width={72} height={72}
               className="rounded-xl ring-1 ring-white/10" />
        </div>

        <div className="relative">
          <h1 className="font-display text-5xl tracking-tight text-[#E8C77A]">JASR HUB</h1>
          <p className="mt-2 text-slate-400">The Operating System of Vida Group</p>

          <ul className="mt-8 space-y-2.5 text-sm text-slate-300">
            {['Real-time fleet telematics',
              'AI maintenance tracking',
              'Driver compliance and payroll'].map((line) => (
              <li key={line} className="flex items-center gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#C59B27] shrink-0" />
                {line}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-slate-500">© Vida Group · Saudi Arabia</p>
      </aside>

      {/* --------------------------------------------------- the form side */}
      <main className="flex flex-col min-h-screen lg:min-h-0">
        <header className="flex items-center justify-end gap-2 p-6">
          <a href="/tv"
            className="inline-flex items-center gap-1.5 h-9 rounded-lg border border-[#E2E8F0] dark:border-white/10
                       px-3 text-sm text-[#64748B] hover:text-[#0F172A] dark:hover:text-white
                       hover:border-[#C59B27] transition-all duration-200">
            <Monitor className="h-4 w-4" /> TV
          </a>
          <LanguageSwitcher />
          <ThemeToggle />
        </header>

        <div className="flex-1 flex items-center justify-center px-6 pb-10">
          <div className="w-full max-w-sm animate-fade-in">
            {/* On a phone the brand panel is gone, so the mark leads here. */}
            <div className="lg:hidden flex flex-col items-center mb-8">
              <img src="/logo.png" alt="JASR HUB" width={64} height={64} className="rounded-xl" />
              <h1 className="font-display text-3xl mt-3 text-[#0F172A] dark:text-white">JASR HUB</h1>
              <p className="text-xs text-[#64748B] mt-1">The Operating System of Vida Group</p>
            </div>

            <h2 className="font-display text-2xl text-[#0F172A] dark:text-white">
              {mode === 'password' ? 'Sign in'
                : mode === 'otp_phone' ? 'Sign in with your phone'
                : 'Enter your code'}
            </h2>
            <p className="text-sm text-[#64748B] mt-1">
              {mode === 'password' ? 'Use your user code or email and password.'
                : mode === 'otp_phone' ? 'We will send a one-time code by SMS.'
                : `Sent to ${phone}`}
            </p>

            {error && (
              <div role="alert"
                className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700
                           dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
                {error}
              </div>
            )}

            {/* ----------------------------------------------- password */}
            {mode === 'password' && (
              <form onSubmit={handlePasswordLogin} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="username" className="text-xs font-medium text-[#0F172A] dark:text-slate-300">
                    Username
                  </label>
                  <div className="relative mt-1.5">
                    <span className={iconWrap}><UserIcon className="h-4 w-4" /></span>
                    <input id="username" className={field} value={username} autoComplete="username"
                      onChange={(e) => setUsername(e.target.value)} placeholder="Code, email or phone" />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="text-xs font-medium text-[#0F172A] dark:text-slate-300">
                    Password
                  </label>
                  <div className="relative mt-1.5">
                    <span className={iconWrap}><Lock className="h-4 w-4" /></span>
                    <input id="password" className={`${field} pe-10`} value={password}
                      type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                      onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                    <button type="button" onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 end-3 flex items-center text-[#94A3B8] hover:text-[#0F172A] dark:hover:text-white transition-colors">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <label className="inline-flex items-center gap-2 text-sm text-[#64748B] cursor-pointer whitespace-nowrap">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                      className="h-4 w-4 rounded border-[#E2E8F0] accent-[#C59B27] shrink-0" />
                    Remember me
                  </label>
                  <span className="text-sm text-[#94A3B8] whitespace-nowrap">
                    Forgot password? <span className="text-[#C59B27]">Ask your admin</span>
                  </span>
                </div>

                <button type="submit" disabled={submitting} className={goldButton}>
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {submitting ? 'Signing in…' : 'Sign in'}
                </button>

                {SHOW_OTP && (
                  <Button type="button" variant="ghost" className="w-full gap-2"
                    onClick={() => { setMode('otp_phone'); setError(null); }}>
                    <Phone className="h-4 w-4" /> {t('auth.usePhone', 'Sign in with your phone')}
                  </Button>
                )}
              </form>
            )}

            {/* --------------------------------------------- otp: phone */}
            {mode === 'otp_phone' && (
              <form onSubmit={handleRequestOtp} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="phone" className="text-xs font-medium text-[#0F172A] dark:text-slate-300">
                    Phone number
                  </label>
                  <div className="relative mt-1.5">
                    <span className={iconWrap}><Phone className="h-4 w-4" /></span>
                    <input id="phone" className={field} value={phone} inputMode="tel" autoComplete="tel"
                      onChange={(e) => setPhone(e.target.value)} placeholder="+9665…" />
                  </div>
                </div>
                <button type="submit" disabled={submitting || !phone.trim()} className={goldButton}>
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send the code
                </button>
                <Button type="button" variant="ghost" className="w-full gap-2"
                  onClick={() => { setMode('password'); setError(null); }}>
                  <ArrowLeft className="h-4 w-4" /> Back to password
                </Button>
              </form>
            )}

            {/* ---------------------------------------------- otp: code */}
            {mode === 'otp_code' && (
              <form onSubmit={handleVerifyOtp} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="code" className="text-xs font-medium text-[#0F172A] dark:text-slate-300">
                    One-time code
                  </label>
                  <div className="relative mt-1.5">
                    <span className={iconWrap}><KeyRound className="h-4 w-4" /></span>
                    <input id="code" ref={codeInputRef} className={`${field} tracking-[0.4em] font-mono`}
                      value={code} inputMode="numeric" autoComplete="one-time-code" maxLength={8}
                      onChange={(e) => setCode(e.target.value)} placeholder="000000" />
                  </div>
                </div>
                <button type="submit" disabled={submitting || !code.trim()} className={goldButton}>
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Verify and sign in
                </button>
                <Button type="button" variant="ghost" className="w-full gap-2"
                  onClick={() => { setMode('otp_phone'); setError(null); setCode(''); }}>
                  <ArrowLeft className="h-4 w-4" /> Use a different number
                </Button>
              </form>
            )}
          </div>
        </div>

        <footer className="px-6 pb-6 text-center lg:text-end text-xs text-[#64748B]">
          © Vida Group • Saudi Arabia | Powered by JASR HUB
        </footer>
      </main>
    </div>
  );
}
